import React, { useEffect, useState } from 'react';
import { Field, FormikProvider, useFormik } from 'formik';
import { Alert, AlertTitle, Box, Card, CircularProgress, Dialog, DialogContent, IconButton, Tooltip } from '@mui/material';
import { ImageField, StripImageField } from '../fields/resultDetailFields';
import api from '../../../redux/features/auth/axiosAuthHelper';
import { InterpretationEnum, Result } from '../CustomHooks';
import ResultDetailGraph from '../graphs/ResultDetailGraph';
import colorConfigs from '../../../configs/colorConfigs';
import VisualReviewCard from './VisualReviewCard';
import ReviewActionsPanel from './ReviewActionsPanel';
import { ResultDetailEditField } from '../fields/fields';
import dayjs from 'dayjs';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ResultDetailGraphBulkReview from '../graphs/ResultDetailGraphBulkReview';

const fetchResult = async (accessToken: string, resultId: string) => {
  let result: Result | null = null;
  let error = '';
  try {
    const url = `results/${resultId}`;

    const response = await api.get(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
    result = response.data;
    if (!result) {
      return null;
    }
  } catch (error) {
    console.error('Error fetching result', error);
    error = `No Result found with ID ${resultId}`;
  }

  return { result, error };
};

interface Props {
  resultId: string;
  value: { interpretation: InterpretationEnum; notes: string };
  onChange: (updates: {
    interpretation: InterpretationEnum;
    notes: string;
  }) => void;
  changeActionSelected: boolean;
  onChangeActionSelected: (update: boolean) => void;
  accessToken: string | null;
}

const ResultReviewItem: React.FC<Props> = ({
  resultId,
  value,
  onChange,
  changeActionSelected,
  onChangeActionSelected,
  accessToken,
}) => {
  const [result, setResult] = useState<Result | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingError, setLoadingError] = useState<boolean>(false);
  const [stripImage, setStripImage] = useState('');
  const isReviewed = !!value?.interpretation || result?.visual_result?.is_reviewed;
  const interpretation =
    value?.interpretation || result?.visual_result?.reviewer_interpretation || result?.visual_result?.visual_interpretation;
  const notes = value?.notes || result?.visual_result?.reviewer_notes || result?.visual_result?.notes;
  const reviewDate = result?.visual_result?.reviewed_timestamp ? dayjs(result?.visual_result?.reviewed_timestamp).format('YYYY-MM-DD HH:mm:ss') : null;

  console.log(
    'ResultReviewItem IN',
    resultId,
    value,
    isReviewed,
    interpretation,
    notes
  );

  const formik = useFormik({
    initialValues: { id: '', sample_id: '', reviewer_notes: '' },
    onSubmit: async (values) => { },
  });

  const [graphData, setGraphData] = useState<any>({
    test_lines: [],
    cscore: '',
    cpeak: '',
    profile: [''],
    baseline: [''],
    c_color: '',
    baseline_color: '',
    profile_color: '',
    profiles: [],
    baselines: [],
    meanProfileVisible: false,
  });

  useEffect(() => {
    if (result) {
      formik.setFieldValue('id', result.id);
      formik.setFieldValue('sample_id', result.sample_id);
      const abortedResult = result.type === 'ABORTED';

      if (result.strip_image_paths && result.strip_image_paths.length) {
        setStripImage(result.strip_image_paths[0]);
      }

      const dynamicProfiles: any[] = [];
      const dynamicBaselines: any[] = [];

      if (!abortedResult && result.test_strips.length > 0) {
        dynamicProfiles.push({
          data: result.test_strips[0].profile,
          color: colorConfigs.result.meanProfile,
          name: 'Mean Profile',
        });
        dynamicBaselines.push({
          data: result.test_strips[0].baseline,
          color: colorConfigs.result.meanBaseline,
          name: 'Mean Baseline',
        });
        const dynamicTestLines = result.test_strips[0].lines.map(
          (line: any) => {
            line['t_color'] = colorConfigs.tables.headBg;
            return line;
          }
        );
        setGraphData({
          test_lines: dynamicTestLines,
          cscore: result.test_strips[0].c_line.score || 0,
          cpeak: result.test_strips[0].c_line.peak_position || 0,
          c_color: colorConfigs.buttons.active,
          profile: result.test_strips[0].profile,
          baseline: result.test_strips[0].baseline,
          baseline_color: colorConfigs.result.meanBaseline,
          profile_color: colorConfigs.result.meanProfile,
          profiles: dynamicProfiles, // Initialize as empty
          baselines: dynamicBaselines, // Initialize as empty
          meanProfileVisible: true,
        });
      }
    }
  }, [result]);

  useEffect(() => {
    formik.setFieldValue('reviewer_notes', notes);
  }, [notes]);

  useEffect(() => {
    const loadResult = async () => {
      try {
        setLoading(true);
        setLoadingError(false);
        let data = null;
        if (accessToken) {
          data = await fetchResult(accessToken, resultId);
        }
        if (!data || data.error) {
          setLoadingError(true);
        } else {
          setResult(data.result);
        }
      } finally {
        setLoading(false);
      }
    };

    loadResult();
  }, [resultId, accessToken]);

  const handleConfirmInterpretation = () => {
    if (result) {
      onChangeActionSelected(false);
      if (result?.visual_result?.reviewer_interpretation) {
        onChange({
          interpretation: result?.visual_result?.reviewer_interpretation,
          notes: `${result?.visual_result?.reviewer_notes || 'Confirmed visual interpretation via bulk operation'}`,
        });
      } else if (result?.visual_result?.visual_interpretation) {
        onChange({
          interpretation: result?.visual_result?.visual_interpretation,
          notes: 'Confirmed visual interpretation via bulk operation',
        });
      } else {
        onChange({
          interpretation: result.app_interpretation,
          notes: 'Confirmed app interpretation via bulk operation',
        });
      }
    }
  };

  const handleChangeInterpretation = (
    newInterpretation: InterpretationEnum,
    newNotes: string
  ) => onChange({ interpretation: newInterpretation, notes: newNotes });

  const [selectedImage, setSelectedImage] = useState<any>("")
  const [openImagePopup, setOpenImagePopup] = useState<any>(false)

  const handleImageOpen = (image: any) => {
    setSelectedImage(image); // Set the selected strip image source
    setOpenImagePopup(true);
  };

  const handleImageClose = () => {
    setOpenImagePopup(false);
  };


  return loadingError ? (
    <Box sx={{ width: '100%' }}>
      <Alert severity="error" variant="outlined">
        <AlertTitle>Failed to load result</AlertTitle>
        {`An unexpected error occurred while loading result ${resultId}.`}
      </Alert>
    </Box>
  ) : !result || loading ? (
    <Card sx={{ p: 2, height: 1000, width: '100%' }}>
      <Box sx={{ width: '100%' }}>
        <CircularProgress />
      </Box>
    </Card>
  ) : (
    <FormikProvider value={formik}>
      <Card sx={{ p: 2, height: '100%', width: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'column', md: 'row' },
            gap: 2,
            width: '100%',
            flexWrap: 'nowrap', // force same row
            overflowX: 'auto',
          }}
        >
          {/* 30% */}
          <Box
            sx={{
              flex: { xs: '1 1 100%', md: '0 1 35%', lg: '0 1 25%' },
              display: 'flex',
              justifyContent: 'center',
              minWidth: 400,
            }}
          >
            <ResultDetailGraphBulkReview graphdata={graphData} showGraph={true} />
          </Box>

          {/* 20% */}
          <Box
            sx={{
              flex: { xs: '1 1 100%', md: '0 1 15%', lg: '0 1 25%' },
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <div className="form-group">
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: -1 }}>
                <Tooltip title="Open result in new tab">
                  <IconButton
                    sx={{ p: 0.5 }}
                    aria-label="Open result details"
                    onClick={() =>
                      window.open(`/results/${resultId}`, '_blank', 'noopener,noreferrer')
                    }
                  >
                    <VisibilityOutlinedIcon />
                  </IconButton>
                </Tooltip>
              </Box>
              <Field
                name="id"
                type="text"
                className="form-control"
                label="Result ID"
                disableAutoFocus={true}
                component={ResultDetailEditField}
              />
              <Field
                name="sample_id"
                type="text"
                className="form-control"
                label="Sample ID"
                disableAutoFocus={true}
                component={ResultDetailEditField}
              />
              {stripImage && (
                <Box onClick={() => handleImageOpen(stripImage)} >
                  <Field
                    name="nothing"
                    image={stripImage}
                    type="text"
                    className="form-control"
                    label="Test strip image 1"
                    component={StripImageField}
                  />
                </Box>
              )}
            </div>

            {/* Dialog for Frame Image Popup */}
            <Dialog data-testid="Dialog-njls"
              open={openImagePopup}
              onClose={handleImageClose}
              maxWidth="md"
              fullWidth
              slotProps={{ backdrop: { onClick: handleImageClose } }}
              sx={{
                "& .MuiDialog-paper": {
                  backgroundColor: "transparent", // Semi-transparent background
                  boxShadow: "none",
                },
              }}
            >
              <DialogContent data-testid="DialogContent-o77v" sx={{ padding: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Box data-testid="Box-3rd9"
                  onClick={handleImageClose}
                  component="img"
                  src={selectedImage}
                  alt="Frame Image"
                  sx={{ width: '100%', height: 'auto', objectFit: 'contain', cursor: 'pointer' }}
                />
              </DialogContent>
            </Dialog>

          </Box>

          {/* 30% */}
          <Box
            // sx={{
            //     flex: '0 0 auto', // no crece, no encoge automáticamente
            //     flexBasis: { md: '30%' },
            //     maxWidth: { md: '30%' },
            //     width: { md: '30%' }, // asegura el ancho
            //   }}
            sx={{
              flex: { xs: '1 1 100%', md: '0 1 26%', lg: '0 1 36%' },
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <VisualReviewCard
              appInterpretation={result.app_interpretation}
              userInterpretation={interpretation}
              userNotes={notes}
              isReviewed={isReviewed}
              reviewDate={reviewDate}
            />
          </Box>

          {/* 10% */}
          <Box
            sx={{
              flex: { xs: '1 1 100%', md: '0 1 14%', lg: '0 1 14%' },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <ReviewActionsPanel
              onConfirm={handleConfirmInterpretation}
              onChange={handleChangeInterpretation}
              initialInterpretation={
                result.visual_result?.reviewer_interpretation ?? result.visual_result?.visual_interpretation ??
                result.app_interpretation
              }
              onChangeActionSelected={onChangeActionSelected}
              changeActionSelected={changeActionSelected}
            />
          </Box>
        </Box>
      </Card >
    </FormikProvider >
  );
};

export default ResultReviewItem;
